// ============================================================
// NewItemsIndicator.tsx
// Stage 4 live-freshness: a small, NON-DESTRUCTIVE "N new {noun} — refresh"
// banner shown atop a filtered/sorted/paginated list when other firm users
// have CREATED entities of that type since the list last loaded. Clicking it
// refetches the list (respecting the viewer's current filters/sort/page) and
// resets the count — we deliberately NEVER blind-insert a new row into a
// filtered list (it might not match the filter / belong on this page).
//
// Pure presentation + one click handler. Renders nothing when count <= 0, so
// it adds zero visual weight in the common (no-new-items) case and never
// disrupts the user's scroll/filter/selection — only an explicit click
// refetches. The count is approximate (see useNewItemsCount); the refetch is
// the source of truth.
// ============================================================
import React from 'react'

interface NewItemsIndicatorProps {
  // How many new items have arrived since the last load (0 → render nothing).
  count: number
  // Singular noun for the entity (e.g. 'case', 'task', 'client'); pluralised
  // with a trailing 's' for count !== 1 (case→cases, task→tasks, client→clients).
  noun: string
  // Refetch the list (respecting current filters/sort/page) AND reset the count.
  onRefresh: () => void
}

const NewItemsIndicator: React.FC<NewItemsIndicatorProps> = ({ count, noun, onRefresh }) => {
  if (count <= 0) return null
  const label = `${count} new ${count === 1 ? noun : `${noun}s`}`
  return (
    <button
      type="button"
      onClick={onRefresh}
      className="mb-4 w-full flex items-center justify-center gap-2 rounded-lg border border-casevo-mint bg-casevo-mint/40 px-4 py-2 text-xs uppercase tracking-widest text-casevo-brand hover:bg-casevo-mint transition-colors"
      aria-live="polite"
    >
      {/* Circular-arrow refresh glyph — inline SVG, no icon dep. */}
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M21 12a9 9 0 1 1-2.64-6.36" strokeLinecap="round" />
        <path d="M21 3v6h-6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {label} — refresh
    </button>
  )
}

export default NewItemsIndicator
